import { usePopover } from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import { ContactOverviewCompanyPopover } from '../contact-overview-company-popover';
import { ContactOverviewItem } from '../contact-overview-item';
import { ContactOverviewJobHistoryPopover } from '../contact-overview-job-history-popover';
import * as Styled from './styles';
import * as SX from './sx';
import type { ContactOverviewProps as Props } from './types';
import { useContactOverviewItems } from './use-contact-overview-items';
import { useHasDefinedItems } from './use-has-defined-items';

export const ContactOverview = memo(({ className }: Props) => {
  const { allCurrentRoles, allPresentRoles, items, moreItems } =
    useContactOverviewItems();
  const showMore = useBoolean(true);
  const jobPopoverState = usePopover();
  const companyPopoverState = usePopover();
  const hasDefinedItems = useHasDefinedItems(items, moreItems);

  return (
    <Styled.Wrapper
      className={classnames('ContactOverview', className)}
      sx={SX.root}
      title="Contact Overview"
      icon="patient-list-outline"
    >
      <Stack>
        {hasDefinedItems &&
          items
            .filter(({ value }) => value)
            .map(
              (
                {
                  icon,
                  label,
                  content,
                  value,
                  badgeLength,
                  condition,
                  type,
                  showPreviousCompanyBadge,
                },
                index,
                filteredItems
              ) => (
                <Box key={label}>
                  <ContactOverviewItem
                    key={label}
                    icon={icon}
                    value={value}
                    label={label}
                    type={type}
                    content={content}
                    badgeLength={badgeLength}
                    condition={condition}
                    onClick={
                      condition
                        ? (event) => {
                            if (type === 'job') {
                              jobPopoverState.onOpen(event.currentTarget);
                            } else if (type === 'company') {
                              companyPopoverState.onOpen(event.currentTarget);
                            }
                          }
                        : undefined
                    }
                    showPreviousCompanyBadge={showPreviousCompanyBadge}
                  />
                  {(index !== filteredItems.length - 1 || showMore.value) && (
                    <Divider />
                  )}
                </Box>
              )
            )}

        {showMore.value &&
          hasDefinedItems &&
          moreItems
            .filter(({ value }) => value)
            .map(
              (
                {
                  icon,
                  label,
                  content,
                  value,
                  badgeLength,
                  condition,
                  type,
                  showPreviousCompanyBadge,
                },
                index,
                filteredItems
              ) => (
                <Box key={label}>
                  <ContactOverviewItem
                    key={label}
                    icon={icon}
                    value={value}
                    label={label}
                    type={type}
                    content={content}
                    badgeLength={badgeLength}
                    condition={condition}
                    showPreviousCompanyBadge={showPreviousCompanyBadge}
                  />
                  {index !== filteredItems.length - 1 && <Divider />}
                </Box>
              )
            )}

        {!hasDefinedItems && (
          <Stack padding={1.5}>
            <Typography variant="body3" color="grey.500">
              There is no information yet
            </Typography>
          </Stack>
        )}
      </Stack>

      {/* {hasDefinedItems && (
        <ShowMoreButton onClick={(value) => showMore.setValue(value)} />
      )} */}

      <ContactOverviewCompanyPopover
        state={companyPopoverState}
        allCurrentRoles={allCurrentRoles}
      />

      <ContactOverviewJobHistoryPopover
        state={jobPopoverState}
        allPresentRoles={allPresentRoles}
      />
    </Styled.Wrapper>
  );
});

ContactOverview.displayName = 'ContactOverview';
